import { createAction } from 'redux-actions';
import { fillUserData as partsFormFillUserData } from 'shared/helpers/partsForm';
import { fillUserData as advertFormFillUserData } from 'shared/helpers/advertForm';
import { getGoogleCaptchaToken, getGoogleCaptchaTokenV2 } from 'shared/helpers/google';
import { enqueueNotification as notification } from 'shared/actions/notifications';
import * as Cookies from 'js-cookie';

import { toLocalized } from 'shared/helpers/locale';
import regexp from 'shared/constants/regexp';
import validator from 'validator';
import objectToFormData from 'object-to-formdata';
import API from 'shared/constants/api';
import history from 'history.js';
import URL from 'shared/constants/url';
import { phoneNormalize } from 'shared/modules';
import { USER } from '../constants/actionTypes';

import { TOKEN_KEY, STO_COOKIE_MAX_AGE_DAYS, STO_TOKEN_KEY } from '../constants/cookies';

const SECONDARY_COOKIE_DOMAIN = process.env.BROWSER ? `.${window.location.hostname}` : undefined;

const L = {
  DEFAULT_ERROR_MESSAGE: {
    ru: 'Извините, произошла ошибка на сервере, попробуйте повторить операцию позже',
    uk: 'Вибачте, сталася помилка на сервері, спробуйте повторити операцію пізніше',
  },
  USER_ALREADY_EXIST_MESSAGE: {
    ru: 'Такой пользователь уже зарегистрирован',
    uk: 'Такий користувач вже зареєстрований',
  },
  CODE_NOT_VALID_MESSAGE: {
    ru: 'Вы ввели неверный код',
    uk: 'Ви ввели невірний код',
  },
  PASSWORD_NOT_VALID_MESSAGE: {
    ru: 'Вы ввели неверный пароль',
    uk: 'Ви ввели невірний пароль',
  },
  INPUT_VALID_USER_MESSAGE: {
    ru: 'Введите мобильный телефон',
    uk: 'Введіть мобільний телефон',
  },
  IMAGE_SIZE_MESSAGE: {
    ru: 'Вес изображения должен быть не больше 1 мб',
    uk: 'Вага зображення повинна бути не більше 1 мб',
  },
  USER_RECOVERY_MESSAGE: data => ({
    ru: `На ${data} отправлен новый пароль`,
    uk: `На ${data} відправлено новий пароль`,
  }),
  USER_RECOVERY_VERIFY: data => ({
    ru: `На ${data} отправлено сообщение для подтверждения почты`,
    uk: `На ${data} відправлено повідомлення для підтвердження пошти`,
  }),
  'User with this phone already registered': 'Пользователь с таким телефоном уже зарегистрирован',
  'User with this email already registered':
    'Пользователь с таким адресом электронной почты уже зарегистрирован',
  'Invalid password for this login': 'Не верная электронная почта или пароль',
  'User with such login does not exist':
    'Указанный телефон или почта не зарегистрирована в системе',
};

export const setSession = createAction(USER.SET_SESSION);
export const setUser = createAction(USER.SET_USER);
export const setPhone = createAction(USER.SET_PHONE);
export const removePhone = createAction(USER.REMOVE_PHONE);
export const userRegistration = createAction(USER.REGISTRATION);
export const beforeRegistration = createAction(USER.BEFORE_REGISTRATION);
export const userError = createAction(USER.ERROR);
export const setSavedSearches = createAction(USER.SET_SAVED_SEARCHES);
export const setShowSaveSearchBtn = createAction(USER.SET_SHOW_SAVE_SEARCH_BTN);
export const userLogout = () => {
  Cookies.remove(TOKEN_KEY);
  Cookies.remove(STO_TOKEN_KEY, { domain: SECONDARY_COOKIE_DOMAIN });

  return {
    type: USER.LOGOUT,
  };
};

export const userLogin = (token, customHeaders = {}, modal, enqueueNotification) => (
  dispatch,
  getState,
  { fetch },
) => {
  const { globals: { locale }, history: { currentPath } } = getState();
  return fetch(API.USER_PROFILE, {
    method: 'GET',
    headers: {
      Authorization: `JWT ${token}`,
      ...customHeaders,
    },
  })
    .then(async response => {
      if (response.ok) {
        Cookies.set(TOKEN_KEY, token);
        Cookies.set(STO_TOKEN_KEY, token, {
          domain: SECONDARY_COOKIE_DOMAIN,
          expires: STO_COOKIE_MAX_AGE_DAYS,
        });

        const json = await response.json();
        dispatch(setUser(json));
        dispatch(setSession(token));
        // start getting redirect url
        const currentUrl = typeof window === 'object' ? window.location.href : '';
        const queryArr = currentUrl.split('?');

        const result = { redirectUrl: '' };
        if (queryArr.length > 1) {
          const vars = queryArr[1].split('&');
          vars.map(item => {
            const pair = item.split('=');
            if (pair[0] === 'redirectUrl') {
              result.redirectUrl = pair[1];
            }
            return false;
          });

          if (result.redirectUrl) {
            window.location.href = result.redirectUrl;
            return false;
          }
        }
        // end getting redirect url
        if (!modal && history) {
          history.push(toLocalized(URL.my(), locale));
        }

        if (process.env.BROWSER && typeof currentPath === 'string' && /\/add/.test(currentPath)) {
          if (/\/add\/zapchasti/.test(currentPath)) {
            partsFormFillUserData(fetch, { dispatch, getState });
          } else {
            advertFormFillUserData(fetch, { dispatch, getState });
          }
        }
        return Promise.resolve(json);
      }

      if (enqueueNotification) {
        enqueueNotification(L.DEFAULT_ERROR_MESSAGE[locale]);
      }

      throw new Error(L.DEFAULT_ERROR_MESSAGE[locale]);
    })
    .catch(err => {
      console.error(err);
      if (enqueueNotification) {
        enqueueNotification(L.DEFAULT_ERROR_MESSAGE[locale]);
      }
    });
};

export const updateUser = (data, { intl, ContactInformation }) => (
  dispatch,
  getState,
  { fetch },
) => {
  const { user: { user }, globals: { locale } } = getState();

  const token = getState().user.token;
  return fetch(API.PROFILE, {
    method: 'PATCH',
    headers: {
      Authorization: `JWT ${token}`,
    },
    body: objectToFormData(data),
  })
    .then(async response => {
      if (response.ok) {
        const json = await response.json();
        const { email, is_email_verified: isEmailVerified } = json;

        if (email && user.email !== email && isEmailVerified === false && ContactInformation) {
          dispatch(
            notification(
              intl.formatMessage(
                { id: 'На {email} отправлено сообщение для подтверждения почты' },
                { email },
              ),
            ),
          );
        }

        dispatch(setUser(json));

        return Promise.resolve();
      }
      throw new Error(
        response.status === 413 ? L.IMAGE_SIZE_MESSAGE[locale] : L.DEFAULT_ERROR_MESSAGE[locale],
      );
    })
    .catch(() => {
      throw new Error(L.DEFAULT_ERROR_MESSAGE[locale]);
    });
};

export const addPhone = ({ phone, advert }) => (dispatch, getState, { fetch }) => {
  const { user: { user: { phones = [] } } } = getState();
  let url = API.PHONES.ADD;
  for (let i = 0; i < phones.length; i += 1) {
    if (phoneNormalize(phones[i].number) === phoneNormalize(phone) && phones[i].is_verified) {
      return new Promise((resolve, reject) => {
        reject({ message: 'Телефон уже подтвержден' });
      });
    }
    if (phoneNormalize(phones[i].number) === phoneNormalize(phone)) {
      url = API.PHONES.RESEND_CODE;
    }
  }
  return fetch(url, {
    method: 'POST',
    body: JSON.stringify({ phone: phoneNormalize(phone) }),
    headers: {
      Authorization: `JWT ${getState().user.token}`,
    },
  }).then(async response => {
    if (response.ok) {
      if (!advert) {
        dispatch(
          setPhone({
            number: phone,
            is_verified: false,
          }),
        );
      }

      return Promise.resolve();
    }

    const { locale } = getState().globals;

    throw new Error(
      response.status === 400
        ? L.USER_ALREADY_EXIST_MESSAGE[locale]
        : L.DEFAULT_ERROR_MESSAGE[locale],
    );
  });
};
export const deletePhone = id => (dispatch, getState, { fetch }) => {
  if (typeof id === 'object') {
    return dispatch(removePhone(id));
  }
  return fetch(API.PHONES.DELETE(id), {
    method: 'DELETE',
    headers: {
      Authorization: `JWT ${getState().user.token}`,
    },
  }).then(async response => {
    if (response.ok) {
      dispatch(removePhone(id));
      // TODO: remove
      dispatch(updateUser({}, {}));

      return Promise.resolve();
    }
    const { locale } = getState().globals;
    throw new Error(L.DEFAULT_ERROR_MESSAGE[locale]);
  });
};

export const authWithPhone = ({ phone, code }) => (dispatch, getState, { fetch }) => {
  const { locale } = getState().globals;
  return fetch(API.PHONES.VERIFY_PHONE, {
    method: 'POST',
    body: JSON.stringify({ phone: phoneNormalize(phone), code }),
    headers: {
      Authorization: `JWT ${getState().user.token}`,
    },
  })
    .then(async response => {
      if (response.status === 200) {
        // TODO: remove
        dispatch(updateUser({}, {}));
        return Promise.resolve();
      }
      throw new Error(
        response.status === 400
          ? L.CODE_NOT_VALID_MESSAGE[locale]
          : L.DEFAULT_ERROR_MESSAGE[locale],
      );
    })
    .catch(() => {
      throw new Error(L.DEFAULT_ERROR_MESSAGE[locale]);
    });
};

export const authorize = ({ thing, code, isPassword = false }) => (dispatch, getState, { fetch }) =>
  getGoogleCaptchaToken('authorize').then(gtoken =>
    fetch(API.AUTHORIZE, {
      method: 'POST',
      headers: { gtoken },
      body: JSON.stringify({ thing, ...(code ? { code } : {}) }),
    }).then(async response => {
      const json = await response.json();

      if (response.ok) {
        if (code) {
          const { token } = json;

          if (token) {
            return dispatch(userLogin(token));
          }
        }

        return Promise.resolve(json);
      }

      let message;
      if (json && Object.keys(json).length > 0) {
        message = json[Object.keys(json)[0]][0];
      }

      const { locale } = getState().globals;

      let error = L.DEFAULT_ERROR_MESSAGE[locale];
      if (isPassword) {
        error = L.PASSWORD_NOT_VALID_MESSAGE[locale];
      } else if (message === 'invalid') {
        error = code ? L.CODE_NOT_VALID_MESSAGE[locale] : L.INPUT_VALID_USER_MESSAGE[locale];
      }

      throw new Error(error);
    }),
  );

export const resendVerifyCode = ({ phone }) => (dispatch, getState, { fetch }) =>
  fetch(API.PHONES.RESEND_CODE, {
    method: 'POST',
    body: JSON.stringify({ phone: phoneNormalize(phone) }),
    headers: {
      Authorization: `JWT ${getState().user.token}`,
    },
  }).then(response => {
    const { locale } = getState().globals;

    return response.ok
      ? Promise.resolve()
      : Promise.reject(new Error(L.DEFAULT_ERROR_MESSAGE[locale]));
  });

export const loginWithFacebookToken = (fbToken, { modal, enqueueNotification }) => (
  dispatch,
  getState,
  { fetch },
) => {
  if (!fbToken) return;
  fetch(API.SOCIAL_LOGIN.FB, {
    method: 'POST',
    body: JSON.stringify({
      token: fbToken,
    }),
  })
    .then(r => {
      if (!r.ok) {
        throw new Error('Не удалось осуществить вход');
      }

      return r.json();
    })
    .then(({ token }) => {
      if (token && typeof token === 'string') {
        dispatch(userLogin(token, null, modal, enqueueNotification));
      }
    })
    .catch(e => enqueueNotification(e.message ? e.message : e));
};
// Balance

// eslint-disable-next-line no-underscore-dangle
const _fetchBalance = (fetch, token) =>
  fetch(API.ACCOUNTS.BALANCE, {
    headers: {
      Authorization: `JWT ${token}`,
    },
  })
    .then(response => {
      if (!response.ok) {
        throw new Error('Не удалось получить баланс пользователя');
      }

      return response;
    })
    .then(r => r.json())
    .then(({ balance }) => balance);

export const setBalance = createAction(USER.SET_BALANCE);
export const setBalanceIsFetching = createAction(USER.SET_BALANCE_IS_FETCHING);

export const fetchBalance = () => (dispatch, getState, { fetch }) => {
  const { user: { token } = {} } = getState();

  if (token) {
    dispatch(setBalanceIsFetching(true));

    _fetchBalance(fetch, token).then(balance => {
      dispatch(setBalance(balance));
      dispatch(setBalanceIsFetching(false));
    });
  }
};

export const reloadUser = () => (dispatch, getState) => {
  const { token } = getState().user;

  return token
    ? dispatch(userLogin(token, null, true)).then(() => dispatch(fetchBalance()))
    : Promise.resolve;
};
export const registration = (data, { setFields, enqueueNotification, intl }) => (
  dispatch,
  getState,
  { fetch },
) => {
  const { globals: { locale }, user } = getState();
  const { gr } = user.beforeRegistration;
  const gtokenv2 = (gr && gr.getResponse()) || '';
  dispatch(beforeRegistration({}));
  getGoogleCaptchaToken('registration').then(tokenv3 => {
    fetch(API.REGISTER, {
      method: 'POST',
      headers: {
        gtoken: tokenv3,
        gtokenv2,
      },
      body: JSON.stringify({
        email: data.email,
        phone: phoneNormalize(data.phone),
        name: data.name,
      }),
    })
      .then(async res => {
        if (res.status === 201) {
          dispatch(beforeRegistration(await res.json()));
        } else if (res.status === 400) {
          const err = (await res.json()) || {};
          const buf = {};
          dispatch(userError(err));
          Object.keys(err).map(a => {
            buf[a] = {
              value: data[a] || '',
              errors: [
                new Error(
                  intl.formatMessage({
                    id:
                      (err[a] && err[a][0] && L[err[a][0]] && L[err[a][0]]) ||
                      (err[a] && err[a][0]) ||
                      'err',
                  }),
                ),
              ],
            };
            return '';
          });
          setFields(buf);
          if (!buf.name && !buf.email && !buf.phone) {
            enqueueNotification(L.DEFAULT_ERROR_MESSAGE[locale]);
          }
        } else {
          console.info('L.DEFAULT_ERROR_MESSAGE[locale]');
          enqueueNotification(L.DEFAULT_ERROR_MESSAGE[locale]);
        }
      })
      .catch(err => {
        console.error(err, 'catch');
        enqueueNotification(L.DEFAULT_ERROR_MESSAGE[locale]);
      });
  });
};
export const userAuthorize = (data, { setFields, modal, enqueueNotification, intl, ...allDat }) => (
  dispatch,
  getState,
  { fetch },
) => {
  const { globals: { locale } } = getState();
  const buf = {};
  getGoogleCaptchaToken('authorize').then(gtoken => {
    fetch(API.USERS_AUTHORIZE, {
      method: 'POST',
      headers: { gtoken },
      body: JSON.stringify({
        thing: phoneNormalize(data.thing),
        password: data.password,
      }),
    })
      .then(async response => {
        if (response.status === 200) {
          const res = await response.json();
          dispatch(userLogin(res.token, null, modal, enqueueNotification));
        } else {
          if (allDat.registrationForm) {
            buf.password = {
              value: data.password || '',
              errors: [
                new Error(
                  intl.formatMessage({
                    id: 'Неверный пароль',
                  }),
                ),
              ],
            };
          } else if (allDat.recoveryForm) {
            buf.password = {
              value: data.password || '',
              errors: [
                new Error(
                  intl.formatMessage({
                    id: 'Неверный пароль',
                  }),
                ),
              ],
            };
          } else {
            buf.password = {
              value: data.password || '',
              errors: [
                new Error(
                  intl.formatMessage({
                    id: 'Неверный пароль',
                  }),
                ),
              ],
            };
          }
          setFields(buf);
        }
      })
      .catch(err => {
        console.error(err, 'catch');
        enqueueNotification(L.DEFAULT_ERROR_MESSAGE[locale]);
      });
  });
};

export const userRecovery = (
  { userCheckCode, thing },
  { setFields, intl, registrationForm, nameFields },
) => (dispatch, getState, { fetch }) => {
  if (!registrationForm) {
    dispatch(beforeRegistration({}));
  }
  const { globals: { locale } } = getState();
  getGoogleCaptchaToken('recovery').then(gtoken => {
    fetch(API.USERS_RECOVERY, {
      method: 'POST',
      headers: { gtoken },
      body: JSON.stringify({
        thing: phoneNormalize(thing),
      }),
    })
      .then(async res => {
        if (res.status === 200) {
          dispatch(notification(L.USER_RECOVERY_MESSAGE(phoneNormalize(thing))[locale]));
          const checkCodeUser = userCheckCode ? 'checkCodeUser' : 'userRecovery';
          if (!registrationForm) {
            dispatch(beforeRegistration({ [nameFields || checkCodeUser]: true }));
          }
        } else {
          const err = (await res.json()) || {};
          const setEror = text => {
            setFields({
              [nameFields || 'emailAndPhone']: {
                value: thing || '',
                errors: [new Error(intl.formatMessage({ id: text }))],
              },
            });
          };
          if (err.non_field_errors && err.non_field_errors[0] && L[err.non_field_errors[0]]) {
            setEror(L[err.non_field_errors[0]]);
          } else if (
            err.non_field_errors &&
            err.non_field_errors[0] &&
            typeof err.non_field_errors[0] === 'string'
          ) {
            setEror(err.non_field_errors[0]);
          } else {
            setEror(
              intl.formatMessage({
                id: 'Ошибка',
              }),
            );
          }
        }
      })
      .catch(err => {
        console.error(err, 'catch');
        dispatch(notification(L.DEFAULT_ERROR_MESSAGE[locale]));
      });
  });
};
export const userVerifyMail = ({ enqueueNotification }) => (dispatch, getState, { fetch }) => {
  const { globals: { locale }, user: { user: { email } } } = getState();
  // getGoogleCaptchaToken('verify').then(token => {
  fetch(API.USERS_VERIFY_RESEND, {
    method: 'POST',
    headers: {},
    body: JSON.stringify({
      email,
    }),
  })
    .then(async res => {
      if (res.status === 200) {
        enqueueNotification(L.USER_RECOVERY_VERIFY(email)[locale]);
        dispatch(beforeRegistration({ userRecovery: true }));
      } else {
        enqueueNotification('Error');
      }
    })
    .catch(err => {
      console.error(err, 'catch');
      enqueueNotification(L.DEFAULT_ERROR_MESSAGE[locale]);
    });
  // });
};
export const userVerifyCode = ({ fetch, store: { dispatch }, query: { code } = {} }) => {
  dispatch(beforeRegistration({}));
  fetch(API.USERS_VERIFY_GET, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      code,
    }),
  }).then(async res => {
    if (res.status === 200) {
      const user = (await res.json()) || {};
      return dispatch(beforeRegistration({ mailVerify: user.email }));
    }
    return false;
  });
};
export const userCheckCode = (
  { callback, thing, registration: registrationBool },
  { intl, setFields, nameFields },
) => (dispatch, getState, { fetch }) => {
  if (!process.env.BROWSER) {
    return null;
  }
  const obj = {};
  if (validator.isEmail(thing)) {
    obj.existsEmail = thing;
  }
  if (regexp.phone.test(thing)) {
    obj.existsPhone = thing;
  }
  obj.existsNot = true;
  const { globals: { locale } } = getState();
  if (registrationBool) {
    dispatch(beforeRegistration(obj));
    return callback();
  }

  return getGoogleCaptchaToken('checkCode').then(gtoken =>
    fetch(API.USERS_USER_CHECK_CODE, {
      method: 'POST',
      headers: { gtoken },
      body: JSON.stringify({
        thing: phoneNormalize(thing),
      }),
    })
      .then(async res => {
        if (res.status === 200) {
          const resAnswer = (await res.json()) || {};
          const { exists, password_assigned: passwordAssigned } = resAnswer;
          if (nameFields) {
            return dispatch(beforeRegistration(resAnswer));
          }
          if (!exists) {
            return dispatch(beforeRegistration(obj));
          }
          if (!passwordAssigned) {
            return userRecovery(
              { thing, userCheckCode: true },
              { intl, setFields },
            )(dispatch, getState, {
              fetch,
            });
          }
          return dispatch(beforeRegistration({ checkCodeUser: true }));
        }
        dispatch(beforeRegistration({}));
        return dispatch(notification(L.DEFAULT_ERROR_MESSAGE[locale]));
      })
      .catch(err => {
        console.error(err, 'catch');
        dispatch(notification(L.DEFAULT_ERROR_MESSAGE[locale]));
      })
      .finally(() => {
        callback();
      }),
  );
};
export const changePassword = ({ callback, data }, { intl, setFields }) => (
  dispatch,
  getState,
  { fetch },
) => {
  const { globals: { locale }, user: { token } } = getState();
  return fetch(API.CHANGE_USER_PASSWORD, {
    method: 'PUT',
    headers: {
      Authorization: `JWT ${token}`,
    },
    body: JSON.stringify(data),
  })
    .then(async res => {
      if (res.status === 200) {
        dispatch(notification(intl.formatMessage({ id: 'Новый пароль успешно сохранен' })));
        callback();
        return dispatch(beforeRegistration());
      }
      if (res.status === 400) {
        if (!data.old_password) {
          return dispatch(notification(L.DEFAULT_ERROR_MESSAGE[locale]));
        }
        return setFields({
          oldPassword: {
            value: data.old_password,
            errors: [new Error(intl.formatMessage({ id: 'Неверный пароль' }))],
          },
        });
      }
      return dispatch(notification(L.DEFAULT_ERROR_MESSAGE[locale]));
    })
    .catch(err => {
      console.error(err, 'catch');
      dispatch(notification(L.DEFAULT_ERROR_MESSAGE[locale]));
    });
};
export const logistics = data => (dispatch, getState, { fetch }) => {
  const { globals: { fingerprint } } = getState();
  fetch(API.USERS_LOGISTICS, {
    method: 'POST',
    headers: {},
    body: JSON.stringify({ fingerprint, ...data }),
  });
};

export const getSingleAuthToken = data => (dispatch, getState, { fetch }) =>
  fetch(API.USERS_AUTHORIZE_TOKEN, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(data),
  }).then(async response => {
    if (response.status === 200) {
      const res = await response.json();
      dispatch(setSession(res.token));
    }
  });

export const renderRecaptchaV2 = id => dispatch =>
  getGoogleCaptchaTokenV2(
    id,
    gr => {
      dispatch(beforeRegistration({ recaptchaReady: true, gr }));
    },
    gtokenv2 => {
      dispatch(beforeRegistration({ gtokenv2 }));
    },
  );



// WEBPACK FOOTER //
// ./src/shared/actions/user.js